import { useState, useCallback, useEffect, useRef } from 'react';
import { getDatabase, ref, push } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { PhotoModel } from '../types/protocols';
import { createPhotoModelFromFile, uploadPhotoToDatabaseAndStorage } from '../Helpers/ImageUploadUtils';
import { analyticsApi } from '../api/analyticsApi';
import { recordActivity } from '../api/activityApi';

export interface PhotoPreview {
    id: string;
    file: File;
    previewUrl: string;
}

export interface UploadProgress {
    current: number;
    total: number;
}

type PhotoUploadContext = 'inspection' | 'work_order' | 'component' | 'building' | 'parking' | 'storage_unit' | 'unit';

interface UsePhotoUploadOptions {
    maxPhotos?: number;
    maxFileSize?: number;
    onError?: (message: string) => void;
    /** Context for analytics tracking. Defaults to 'component'. */
    analyticsContext?: PhotoUploadContext;
}

interface UploadConfig {
    storagePath: (photoKey: string) => string;
    dbPath: (photoKey: string) => string;
}

const DEFAULT_MAX_PHOTOS = 10;
const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const CONCURRENT_UPLOADS = 3;

/**
 * Hook for managing photo uploads with drag-and-drop support and parallel batch uploads.
 *
 * Features:
 * - File validation (type and size)
 * - Drag and drop support
 * - Parallel batch uploads with controlled concurrency
 * - Progress tracking
 * - Automatic cleanup of Object URLs on unmount
 */
export function usePhotoUpload(options: UsePhotoUploadOptions = {}) {
    const {
        maxPhotos = DEFAULT_MAX_PHOTOS,
        maxFileSize = DEFAULT_MAX_FILE_SIZE,
        onError,
        analyticsContext = 'component'
    } = options;

    const [photos, setPhotos] = useState<PhotoPreview[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Cleanup Object URLs on unmount
    useEffect(() => {
        return () => {
            photos.forEach(photo => URL.revokeObjectURL(photo.previewUrl));
        };
    }, [photos]);

    const validateFile = useCallback((file: File): string | null => {
        if (!file.type.startsWith('image/')) {
            return 'Endast bilder är tillåtna';
        }
        if (file.size > maxFileSize) {
            return `Filen ${file.name} är för stor (max ${Math.round(maxFileSize / 1024 / 1024)} MB)`;
        }
        return null;
    }, [maxFileSize]);

    const addPhotos = useCallback((files: FileList | File[]) => {
        const fileArray = Array.from(files);
        const remainingSlots = maxPhotos - photos.length;

        if (fileArray.length > remainingSlots) {
            onError?.(`Max ${maxPhotos} bilder tillåtna. Du kan lägga till ${remainingSlots} till.`);
            return;
        }

        const newPhotos: PhotoPreview[] = [];
        for (const file of fileArray) {
            const validationError = validateFile(file);
            if (validationError) {
                onError?.(validationError);
                continue;
            }

            newPhotos.push({
                id: `${Date.now()}-${Math.random()}`,
                file,
                previewUrl: URL.createObjectURL(file)
            });
        }

        if (newPhotos.length > 0) {
            setPhotos(prev => [...prev, ...newPhotos]);
        }
    }, [maxPhotos, photos.length, validateFile, onError]);

    const removePhoto = useCallback((id: string) => {
        setPhotos(prev => {
            const photo = prev.find(p => p.id === id);
            if (photo) {
                URL.revokeObjectURL(photo.previewUrl);
            }
            return prev.filter(p => p.id !== id);
        });
    }, []);

    const clearPhotos = useCallback(() => {
        photos.forEach(photo => URL.revokeObjectURL(photo.previewUrl));
        setPhotos([]);
    }, [photos]);

    // Drag and drop handlers
    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDragOver(false);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
        if (files.length > 0) {
            addPhotos(files);
        }
    }, [addPhotos]);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            addPhotos(e.target.files);
        }
        e.target.value = '';
    }, [addPhotos]);

    const openFilePicker = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    /**
     * Upload all photos in parallel batches with controlled concurrency.
     * Returns a dictionary of uploaded photos keyed by their Firebase keys.
     */
    const uploadPhotos = useCallback(async (config: UploadConfig): Promise<{ [key: string]: PhotoModel }> => {
        if (photos.length === 0) {
            return {};
        }

        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad för att ladda upp bilder');
        }

        const db = getDatabase();
        const uploadedPhotos: { [key: string]: PhotoModel } = {};
        let completedCount = 0;
        let uploadedCount = 0;
        let buildingKey: string | undefined;

        setIsUploading(true);
        setUploadProgress({ current: 0, total: photos.length });

        try {
            // Process photos in batches of CONCURRENT_UPLOADS for parallel upload
            for (let i = 0; i < photos.length; i += CONCURRENT_UPLOADS) {
                const batch = photos.slice(i, i + CONCURRENT_UPLOADS);

                const results = await Promise.all(
                    batch.map(async (photo) => {
                        const photosRef = ref(db, 'inspections/temp');
                        const newPhotoRef = push(photosRef);
                        const photoKey = newPhotoRef.key!;

                        const storagePath = config.storagePath(photoKey);
                        const dbPath = config.dbPath(photoKey);

                        const photoModel = await createPhotoModelFromFile(
                            photoKey,
                            storagePath,
                            user.uid,
                            photo.file
                        );

                        const result = await uploadPhotoToDatabaseAndStorage(dbPath, storagePath, photoModel, photo.file);
                        if (result.success) {
                            uploadedCount++;
                            buildingKey = buildingKey ?? /^buildings\/([^/]+)\//.exec(dbPath)?.[1];
                        }

                        completedCount++;
                        setUploadProgress({ current: completedCount, total: photos.length });

                        return { key: photoKey, model: photoModel };
                    })
                );

                results.forEach(r => {
                    uploadedPhotos[r.key] = r.model;
                });
            }

            // Track successful photo upload
            analyticsApi.trackPhotoUploaded(analyticsContext);
            // One entry per batch; the shared helper does not log, its other callers log their own uploads.
            if (uploadedCount > 0) recordActivity('photo.uploaded', { buildingKey });

            return uploadedPhotos;
        } finally {
            setIsUploading(false);
            setUploadProgress(null);
        }
    }, [photos, analyticsContext]);

    return {
        // State
        photos,
        isDragOver,
        isUploading,
        uploadProgress,
        fileInputRef,

        // Actions
        addPhotos,
        removePhoto,
        clearPhotos,
        uploadPhotos,
        openFilePicker,
        handleFileSelect,

        // Drag handlers
        dragHandlers: {
            onDragOver: handleDragOver,
            onDragLeave: handleDragLeave,
            onDrop: handleDrop
        }
    };
}
